import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langDesktopRef = useRef(null);
  const langMobileRef = useRef(null);
  const { lang, setLang, t, languages } = useLanguage();

  const navigation = [
    { label: t.nav.motionLab, href: '#motion-lab', external: 'https://gurzimotionlab.com/' },
    { label: t.nav.academy, href: '#academy' },
    { label: t.nav.scouting, href: '#scouting' },
    { label: t.nav.goats, href: '#goats' },
    { label: t.nav.contact, href: '#kontakt' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isInsideDesktop = langDesktopRef.current && langDesktopRef.current.contains(e.target);
      const isInsideMobile = langMobileRef.current && langMobileRef.current.contains(e.target);
      if (!isInsideDesktop && !isInsideMobile) {
        setLangOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    setMobileOpen(false);
    if (item.external) {
      window.open(item.external, '_blank');
      return;
    }
    const el = document.querySelector(item.href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const currentLang = languages.find(l => l.code === lang);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[#C9A84C]/10 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center gap-2 group">
          <span className="text-2xl font-bold tracking-tight text-[#C9A84C] font-heading">
            Next Level
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
          {navigation.map((item) => (
            <a
              key={item.href}
              href={item.external || item.href}
              onClick={(e) => handleNavClick(e, item)}
              className="text-sm font-medium text-[#8A8680] hover:text-[#C9A84C] transition-colors duration-300 tracking-wide uppercase"
            >
              {item.label}
            </a>
          ))}

          {/* Language Switcher */}
          <div className="relative" ref={langDesktopRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); }}
              className="flex items-center gap-1.5 text-sm text-[#8A8680] hover:text-[#C9A84C] transition-colors duration-300 px-2 py-1 rounded-md"
            >
              <span className="text-base">{currentLang?.flag}</span>
              <span className="uppercase text-xs font-medium">{lang}</span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-[#111111]/95 backdrop-blur-xl border border-[#C9A84C]/15 rounded-xl shadow-2xl py-2 w-48 max-h-[400px] overflow-y-auto z-50">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={(e) => { e.stopPropagation(); setLang(l.code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors duration-200 ${
                      lang === l.code
                        ? 'text-[#C9A84C] bg-[#C9A84C]/8'
                        : 'text-[#9A9590] hover:text-[#F5F0E8] hover:bg-[#1A1A1A]'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={(e) => handleNavClick(e, '#kontakt')}
            className="bg-[#C9A84C] hover:bg-[#D4B85A] text-[#0A0A0A] font-semibold px-6 h-10 text-sm tracking-wide border-0"
          >
            {t.nav.earlyAccess}
          </Button>
        </nav>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-3">
          {/* Mobile Language */}
          <div className="relative" ref={langMobileRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); }}
              className="flex items-center gap-1 text-[#8A8680] p-2"
            >
              <span className="text-base">{currentLang?.flag}</span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-[#111111]/95 backdrop-blur-xl border border-[#C9A84C]/15 rounded-xl shadow-2xl py-2 w-48 max-h-[320px] overflow-y-auto z-50">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={(e) => { e.stopPropagation(); setLang(l.code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors duration-200 ${
                      lang === l.code
                        ? 'text-[#C9A84C] bg-[#C9A84C]/8'
                        : 'text-[#9A9590] hover:text-[#F5F0E8] hover:bg-[#1A1A1A]'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="text-[#F5F0E8] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="px-6 pb-6 pt-4 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-[#C9A84C]/10 flex flex-col gap-4">
          {navigation.map((item) => (
            <a
              key={item.href}
              href={item.external || item.href}
              onClick={(e) => handleNavClick(e, item)}
              className="text-sm font-medium text-[#8A8680] hover:text-[#C9A84C] transition-colors duration-300 tracking-wide uppercase py-2"
            >
              {item.label}
            </a>
          ))}
          <Button
            onClick={(e) => handleNavClick(e, '#kontakt')}
            className="bg-[#C9A84C] hover:bg-[#D4B85A] text-[#0A0A0A] font-semibold px-6 h-10 text-sm tracking-wide border-0 w-full mt-2"
          >
            {t.nav.earlyAccess}
          </Button>
        </nav>
      </div>
    </header>
  );
};
